/**
 * Drakkar Mapper adapter plugin for TileForge Studio.
 * Gated by 'drakkar' feature entitlement + MFA requirement.
 * Invisible to users without access — no UI trace, no menu items, nothing.
 * Access can only be granted by the super admin (bruno@civongroup.com).
 *
 * Provides:
 * - Import/export of binary Drakkar level files (200x200, 24 bytes/cell)
 * - Cell properties mapped to TileForge custom properties
 * - Generator/trap CSV import as object layers
 * - Wall auto-merge preview overlay
 * - DAT/NDX tile loading (on-demand)
 * - Visibility and flags overlays
 */

// ---------- Constants ----------

const GRID_W = 200;
const GRID_H = 200;
const CELL_BYTES = 24;
const LEVEL_BYTES = GRID_W * GRID_H * CELL_BYTES; // 960,000

const WALL_MERGE_TER_TYPES = new Set([4, 7, 15, 16, 17, 21, 22, 24, 25, 26]);
const WALL_MERGE_BASE_TILES = new Set([33, 73, 93]);

const WALL_MERGE_OFFSETS_CARDINAL = [
  0,   // 0  ---- isolated
  12,  // 1  N
  13,  // 2  E
  9,   // 3  NE
  10,  // 4  S
  14,  // 5  NS
  6,   // 6  ES
  5,   // 7  NES
  11,  // 8  W
  8,   // 9  NW
  4,   // 10 EW
  4,   // 11 NEW
  7,   // 12 SW
  3,   // 13 NSW
  2,   // 14 ESW
  1,   // 15 NESW
];

// Terrain types whose default visibility is Blocked when raw vis == 0
const VIS_DEFAULT_BLOCKED = new Set([0, 4, 7, 16, 17]);

// Flag bit names (18 named bits)
const TFLAG_NAMES = [
  'TELEPORT', 'SAFE', 'NO_MAGIC', 'NO_COMBAT',
  'NO_RECALL', 'DARK', 'UNDERWATER', 'HAZARD',
  'SLIP_N', 'SLIP_E', 'SLIP_S', 'SLIP_W',
  'SLOW', 'FAST', 'PVP', 'NO_MOUNT',
  'NO_SUMMON', 'GUILD_HALL',
];

// NDX parsing constants
const NDX_HEADER_BYTES = 8;
const NDX_NAMED_ENTRIES = 18;
const NDX_NAMED_ENTRY_BYTES = 8;
const NDX_SPECIAL_TILES = 14;
const NDX_BLOCK_SIZE = 1798; // u16 values per block
const NDX_SECTION_A_SIZE = 900; // u16 values
const NDX_SECTION_B_SIZE = 898; // u16 values (449 u32)
const NDX_BLOCKS = 58;

const TILE_PIXEL_SIZE = 64;
const TILE_HEADER_BYTES = 26;
const TILE_DATA_BYTES = TILE_PIXEL_SIZE * TILE_PIXEL_SIZE * 3; // 12,288
const TILE_TOTAL_BYTES = TILE_HEADER_BYTES + TILE_DATA_BYTES; // 12,314
const DAT_GLOBAL_HEADER = 16;

// ---------- Cell helpers ----------

/** Read a u16 LE from a DataView. */
function u16(dv, off) {
  return dv.getUint16(off, true);
}

/** Write a u16 LE into a DataView. */
function setU16(dv, off, val) {
  dv.setUint16(off, val, true);
}

/** Read a u32 LE from a DataView. */
function u32(dv, off) {
  return dv.getUint32(off, true);
}

/** Parse a single 24-byte cell from an ArrayBuffer view. */
function parseCell(dv, baseOff) {
  return {
    terrain_type: u16(dv, baseOff + 0),
    field_2_3: u16(dv, baseOff + 2),
    description: u16(dv, baseOff + 4),
    scenario: u16(dv, baseOff + 6),
    visibility: dv.getUint8(baseOff + 8),
    byte9: dv.getUint8(baseOff + 9),
    lighting: u16(dv, baseOff + 10),
    flags: u32(dv, baseOff + 12),
    hex_size: dv.getUint8(baseOff + 16),
    ter_page: dv.getUint8(baseOff + 17),
    ter_tile: dv.getUint8(baseOff + 18),
    obj_page: dv.getUint8(baseOff + 19),
    obj_tile: dv.getUint8(baseOff + 20),
    ter2: dv.getUint8(baseOff + 21),
    obj2: dv.getUint8(baseOff + 22),
    byte23: dv.getUint8(baseOff + 23),
  };
}

/** Compute ter value from page+tile (NOT u16 LE). */
function terValue(page, tile) {
  return page * 256 + tile;
}

/** Compute obj value from page+tile. */
function objValue(page, tile) {
  return page * 256 + tile;
}

/** Effective visibility: raw 0 derives from terrain type. */
function effectiveVisibility(rawVis, terrainType) {
  const v = rawVis & 0x03;
  if (v !== 0) return v;
  return VIS_DEFAULT_BLOCKED.has(terrainType) ? 1 : 3;
}

/** Format flags as a list of active flag names. */
function formatFlags(flagsInt) {
  const active = [];
  for (let i = 0; i < TFLAG_NAMES.length; i++) {
    if (flagsInt & (1 << i)) active.push(TFLAG_NAMES[i]);
  }
  return active;
}

// ---------- NDX/DAT Tile Index ----------

class DrakkarTileIndex {
  constructor() {
    this.terToOffset = new Map(); // ter value -> byte offset in DAT
  }

  /** Parse an NDX file buffer. */
  parse(buffer) {
    const dv = new DataView(buffer);
    const dataStart = NDX_HEADER_BYTES + NDX_NAMED_ENTRIES * NDX_NAMED_ENTRY_BYTES;
    // Skip special tiles (14 entries * 4 u16 each = 56 u16 = 112 bytes)
    const blocksStart = dataStart + NDX_SPECIAL_TILES * 4 * 2;

    for (let b = 0; b < NDX_BLOCKS; b++) {
      const blockByteOff = blocksStart + b * NDX_BLOCK_SIZE * 2;
      // Section A: count, then ter values, then 0xFFFF padding
      const count = dv.getUint16(blockByteOff, true);
      const terValues = [];
      for (let i = 0; i < count; i++) {
        terValues.push(dv.getUint16(blockByteOff + 2 + i * 2, true));
      }
      // Section B starts at offset NDX_SECTION_A_SIZE u16 from block start
      const sectionBOff = blockByteOff + NDX_SECTION_A_SIZE * 2;
      for (let i = 0; i < terValues.length; i++) {
        const ter = terValues[i];
        if (!this.terToOffset.has(ter)) {
          // Read u32 LE offset
          const datOffset = dv.getUint32(sectionBOff + i * 4, true);
          this.terToOffset.set(ter, datOffset);
        }
      }
    }
  }
}

class DrakkarTileLoader {
  constructor() {
    this.index = null;
    this._datBuffer = null;
    this._cache = new Map(); // ter -> ImageData
  }

  /** Set the parsed index. */
  setIndex(index) {
    this.index = index;
  }

  /** Set the DAT file buffer (ArrayBuffer). Kept as reference for on-demand reads. */
  setDATBuffer(buffer) {
    this._datBuffer = buffer;
  }

  get isReady() {
    return this.index !== null && this._datBuffer !== null;
  }

  /** Load a single 64x64 tile as an ImageData. Returns null if not found. */
  loadTile(terValue) {
    if (!this.isReady) return null;
    if (this._cache.has(terValue)) return this._cache.get(terValue);

    const offset = this.index.terToOffset.get(terValue);
    if (offset === undefined) return null;

    const tileStart = offset + TILE_HEADER_BYTES;
    if (tileStart + TILE_DATA_BYTES > this._datBuffer.byteLength) return null;

    const bgr = new Uint8Array(this._datBuffer, tileStart, TILE_DATA_BYTES);
    const imageData = new ImageData(TILE_PIXEL_SIZE, TILE_PIXEL_SIZE);
    const rgba = imageData.data;

    for (let i = 0; i < TILE_PIXEL_SIZE * TILE_PIXEL_SIZE; i++) {
      const si = i * 3;
      const di = i * 4;
      const b = bgr[si];
      const g = bgr[si + 1];
      const r = bgr[si + 2];
      rgba[di] = r;
      rgba[di + 1] = g;
      rgba[di + 2] = b;
      // Transparent if all channels < 8
      rgba[di + 3] = (r < 8 && g < 8 && b < 8) ? 0 : 255;
    }

    this._cache.set(terValue, imageData);
    return imageData;
  }

  /** Create a tileset canvas from a range of ter values. */
  createTilesetImage(terValues) {
    const cols = 16;
    const rows = Math.ceil(terValues.length / cols);
    const canvas = document.createElement('canvas');
    canvas.width = cols * TILE_PIXEL_SIZE;
    canvas.height = rows * TILE_PIXEL_SIZE;
    const ctx = canvas.getContext('2d');

    for (let i = 0; i < terValues.length; i++) {
      const imageData = this.loadTile(terValues[i]);
      if (imageData) {
        const cx = (i % cols) * TILE_PIXEL_SIZE;
        const cy = Math.floor(i / cols) * TILE_PIXEL_SIZE;
        ctx.putImageData(imageData, cx, cy);
      }
    }

    return canvas;
  }
}

// ---------- Main Plugin ----------

export class DrakkarAdapter {
  constructor(auth) {
    this._auth = auth;
    this.pluginAPI = null;
    this._cells = null;        // 2D array [y][x] of parsed cells
    this._rawBuffer = null;    // Original ArrayBuffer for byte-perfect save
    this._currentLevel = -1;
    this._generators = [];
    this._traps = [];
    this._showVisOverlay = false;
    this._showFlagsOverlay = false;
    this._showWallMerge = false;
    this._tileIndex = new DrakkarTileIndex();
    this._tileLoader = new DrakkarTileLoader();
    this._overlayObj = null;
  }

  init(pluginAPI) {
    // Security gate — must have 'drakkar' entitlement AND MFA enabled
    if (!this._auth) return;
    const access = this._auth.checkFeatureAccess('drakkar');
    if (!access.allowed) return;

    this.pluginAPI = pluginAPI;

    // Register Drakkar cell property object type
    pluginAPI.registerObjectType('drakkar_generator', {
      properties: [
        { name: 'generatorName', type: 'string', default: '' },
        { name: 'bias', type: 'int', default: 0 },
        { name: 'rounds', type: 'int', default: 0 },
        { name: 'max_count', type: 'int', default: 0 },
        { name: 'level', type: 'int', default: 0 },
      ],
      color: 'rgba(255, 100, 0, 0.4)',
      icon: '\u2699',
    });

    pluginAPI.registerObjectType('drakkar_trap', {
      properties: [
        { name: 'trapName', type: 'string', default: '' },
        { name: 'level', type: 'int', default: 0 },
      ],
      color: 'rgba(255, 0, 0, 0.4)',
      icon: '\u26A0',
    });

    // Register overlay
    this._overlayObj = {
      render: (ctx, state, viewport) => this._renderOverlays(ctx, state),
    };
    pluginAPI.registerOverlay(this._overlayObj);

    // Register File menu items under Drakkar section header
    pluginAPI.registerMenuItems('file', [
      { label: 'Open Drakkar Level...', action: () => this._openLevel() },
      { label: 'Save Drakkar Level...', action: () => this._saveLevel() },
      { label: 'Load DAT/NDX Graphics...', action: () => this._loadGraphics() },
      { label: 'Import Generators/Traps...', action: () => this._importGeneratorsTraps() },
    ], 'Kingdom of Drakkar');

    // Register View menu items under Drakkar section header
    pluginAPI.registerMenuItems('view', [
      { label: 'Visibility Overlay', action: () => this._toggleVisOverlay() },
      { label: 'Flags Overlay', action: () => this._toggleFlagsOverlay() },
      { label: 'Wall Merge Preview', action: () => this._toggleWallMerge() },
    ], 'Kingdom of Drakkar');

    // Intercept editor operations to enforce Drakkar constraints when a level is loaded
    this._enforceConstraints();

    // Inject Drakkar worlds into the engine presets dropdown
    this._injectWorldPresets();
  }

  // ---------- World Presets ----------

  _injectWorldPresets() {
    // Dynamically add Drakkar worlds to ENGINE_PRESETS
    // These appear in the New Map dialog and target engine selector
    try {
      import('../ui/EnginePresets.js').then(({ ENGINE_PRESETS }) => {
        const worlds = [
          { key: 'drakkar_nork',     label: 'Drakkar — Nork',     dir: 'nork' },
          { key: 'drakkar_nork3',    label: 'Drakkar — Nork3',    dir: 'nork3' },
          { key: 'drakkar_aleria',   label: 'Drakkar — Aleria',   dir: 'aleria' },
          { key: 'drakkar_cobrahn',  label: 'Drakkar — Cobrahn',  dir: 'cobrahn' },
          { key: 'drakkar_nameless', label: 'Drakkar — Nameless',  dir: 'nameless' },
          { key: 'drakkar_pit',      label: 'Drakkar — Pit',      dir: 'pit' },
          { key: 'drakkar_cob',      label: 'Drakkar — Cob',      dir: 'cob' },
        ];

        for (const world of worlds) {
          ENGINE_PRESETS[world.key] = {
            label: world.label,
            tileSizes: [64],
            defaultTileSize: 64,
            spacing: 0,
            margin: 0,
            notes: `Kingdom of Drakkar — ${world.dir} world. 200x200 grid, 64x64 tiles, 24-byte cells. Binary level format (level.000-006).`,
            drakkarWorld: world.dir,
            drakkar: true,
          };
        }
      });
    } catch (e) {
      // Silently fail — presets are optional
    }
  }

  // ---------- Constraint Enforcement ----------

  /**
   * When a Drakkar level is active, enforce:
   * - Fixed 200x200 grid (no resize)
   * - Fixed 4 layers: Ground (ter), Objects (obj), Ter2, Obj2 (no add/remove)
   * - No TMJ/TMX export (only binary level export)
   * - Cell data (24 bytes) preserved on all operations
   */
  _enforceConstraints() {
    const state = this.pluginAPI.getState();

    // Lock down layer operations when Drakkar mode is active
    state.onChange('*', (value, old, key) => {
      if (!this._isDrakkarMode()) return;

      // Prevent map resize
      if (key === 'mapWidth' && value !== GRID_W) {
        state._data.mapWidth = GRID_W;
        this._showConstraintWarning('Map size is locked to 200x200 in Drakkar mode.');
        return;
      }
      if (key === 'mapHeight' && value !== GRID_H) {
        state._data.mapHeight = GRID_H;
        this._showConstraintWarning('Map size is locked to 200x200 in Drakkar mode.');
        return;
      }
    });

    // Override layer model methods when Drakkar mode active
    this._patchLayerModel();
  }

  _isDrakkarMode() {
    return this._rawBuffer !== null && this._cells !== null;
  }

  _patchLayerModel() {
    // We can't directly patch LayerModel, but we can intercept via state listener
    const state = this.pluginAPI.getState();
    const self = this;

    state.onChange('layers', (layers) => {
      if (!self._isDrakkarMode()) return;

      // Enforce exactly 4 layers: Ground, Objects, Ter2, Obj2
      // Plus optional object layers for generators/traps
      const REQUIRED_LAYERS = ['Ground', 'Objects', 'Ter2', 'Obj2'];
      const tileLayerCount = layers.filter(l => l.type === 'tilelayer').length;

      if (tileLayerCount > 4) {
        // Remove extra tile layers (keep the first 4)
        let removed = false;
        for (let i = layers.length - 1; i >= 0; i--) {
          if (layers[i].type === 'tilelayer' && !REQUIRED_LAYERS.includes(layers[i].name)) {
            layers.splice(i, 1);
            removed = true;
          }
        }
        if (removed) {
          self._showConstraintWarning('Drakkar maps are limited to 4 tile layers: Ground, Objects, Ter2, Obj2.');
        }
      }
    });
  }

  _showConstraintWarning(message) {
    // Debounce warnings
    if (this._lastWarning === message && Date.now() - this._lastWarningTime < 2000) return;
    this._lastWarning = message;
    this._lastWarningTime = Date.now();

    // Show non-modal notification
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed; bottom: 40px; left: 50%; transform: translateX(-50%);
      background: #1c2333; border: 1px solid rgba(248, 81, 73, 0.5);
      color: #f2e9dd; padding: 10px 20px; border-radius: 8px;
      font-size: 12px; z-index: 10000; box-shadow: 0 4px 16px rgba(0,0,0,0.4);
      font-family: 'Inter', sans-serif; max-width: 400px; text-align: center;
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  destroy() {
    this._cells = null;
    this._rawBuffer = null;
    this._generators = [];
    this._traps = [];
    this._tileLoader._cache.clear();

    // Remove injected Drakkar presets
    try {
      import('../ui/EnginePresets.js').then(({ ENGINE_PRESETS }) => {
        for (const key of Object.keys(ENGINE_PRESETS)) {
          if (key.startsWith('drakkar_')) delete ENGINE_PRESETS[key];
        }
      });
    } catch (e) { /* ignore */ }
  }

  // ---------- Level Import ----------

  async _openLevel() {
    const [handle] = await window.showOpenFilePicker({
      types: [{
        description: 'Drakkar Level Files',
        accept: { 'application/octet-stream': ['.000', '.001', '.002', '.003', '.004', '.005', '.006'] },
      }],
    }).catch(() => []);
    if (!handle) return;

    const file = await handle.getFile();
    const buffer = await file.arrayBuffer();
    this._importLevelBuffer(buffer, file.name);
  }

  _importLevelBuffer(buffer, filename) {
    if (buffer.byteLength < LEVEL_BYTES) {
      alert(`File too small: expected at least ${LEVEL_BYTES} bytes, got ${buffer.byteLength}`);
      return;
    }

    // Keep raw buffer for byte-perfect save
    this._rawBuffer = new ArrayBuffer(LEVEL_BYTES);
    new Uint8Array(this._rawBuffer).set(new Uint8Array(buffer, 0, LEVEL_BYTES));

    const dv = new DataView(this._rawBuffer);
    this._cells = [];

    for (let y = 0; y < GRID_H; y++) {
      const row = [];
      for (let x = 0; x < GRID_W; x++) {
        const off = (y * GRID_W + x) * CELL_BYTES;
        row.push(parseCell(dv, off));
      }
      this._cells.push(row);
    }

    // Extract level number from filename
    const match = filename.match(/\.(\d{3})$/);
    this._currentLevel = match ? parseInt(match[1], 10) : -1;

    // Build TileForge map from parsed data
    this._buildTileForgeMap();
  }

  _buildTileForgeMap() {
    const state = this.pluginAPI.getState();

    // Create a 200x200 map with 64x64 tile size (matching Drakkar tiles)
    state.set('mapWidth', GRID_W);
    state.set('mapHeight', GRID_H);
    state.set('tileWidth', TILE_PIXEL_SIZE);
    state.set('tileHeight', TILE_PIXEL_SIZE);

    // Build tile layers
    const terLayer = {
      name: 'Ground',
      type: 'tilelayer',
      visible: true,
      locked: false,
      opacity: 1,
      data: new Array(GRID_W * GRID_H).fill(0),
      properties: {},
    };

    const objLayer = {
      name: 'Objects',
      type: 'tilelayer',
      visible: true,
      locked: false,
      opacity: 1,
      data: new Array(GRID_W * GRID_H).fill(0),
      properties: {},
    };

    const ter2Layer = {
      name: 'Ter2',
      type: 'tilelayer',
      visible: true,
      locked: false,
      opacity: 1,
      data: new Array(GRID_W * GRID_H).fill(0),
      properties: {},
    };

    const obj2Layer = {
      name: 'Obj2',
      type: 'tilelayer',
      visible: true,
      locked: false,
      opacity: 1,
      data: new Array(GRID_W * GRID_H).fill(0),
      properties: {},
    };

    // Cell properties layer (object layer with per-tile custom properties)
    const propsLayer = {
      name: 'Drakkar Properties',
      type: 'objectgroup',
      visible: false,
      opacity: 1,
      objects: [],
    };

    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        const cell = this._cells[y][x];
        const idx = y * GRID_W + x;

        // Map ter/obj to GIDs (use page*256+tile as GID, +1 for Tiled convention where 0=empty)
        const ter = terValue(cell.ter_page, cell.ter_tile);
        const obj = objValue(cell.obj_page, cell.obj_tile);

        terLayer.data[idx] = ter > 0 ? ter + 1 : 0;
        objLayer.data[idx] = obj > 0 ? obj + 1 : 0;
        ter2Layer.data[idx] = cell.ter2 > 0 ? cell.ter2 + 1 : 0;
        obj2Layer.data[idx] = cell.obj2 > 0 ? cell.obj2 + 1 : 0;

        // Only create property objects for non-empty cells
        if (cell.terrain_type !== 0 || cell.description !== 0 || cell.scenario !== 0 ||
            cell.visibility !== 0 || cell.flags !== 0) {
          propsLayer.objects.push({
            id: idx,
            name: `cell_${x}_${y}`,
            type: 'drakkar_cell',
            x: x * TILE_PIXEL_SIZE,
            y: y * TILE_PIXEL_SIZE,
            width: TILE_PIXEL_SIZE,
            height: TILE_PIXEL_SIZE,
            properties: {
              terrain_type: cell.terrain_type,
              description: cell.description,
              scenario: cell.scenario,
              visibility: cell.visibility,
              effective_visibility: effectiveVisibility(cell.visibility, cell.terrain_type),
              lighting: cell.lighting,
              flags: cell.flags,
              flags_text: formatFlags(cell.flags).join(', '),
              hex_size: cell.hex_size,
            },
          });
        }
      }
    }

    const layers = [terLayer, objLayer, ter2Layer, obj2Layer, propsLayer];
    state.set('layers', layers);
    state.set('activeLayerIndex', 0);
    state.set('dirty', false);

    // If tile loader is ready, build tileset images
    if (this._tileLoader.isReady) {
      this._buildTilesetFromLoadedTiles();
    }

    this.pluginAPI.requestRender();
  }

  // ---------- Level Export ----------

  async _saveLevel() {
    if (!this._cells) {
      alert('No Drakkar level loaded. Open a level file first.');
      return;
    }

    // Build output buffer from current state
    const outBuffer = this._buildOutputBuffer();

    const suggestedName = this._currentLevel >= 0
      ? `level.${String(this._currentLevel).padStart(3, '0')}`
      : 'level.000';

    try {
      const handle = await window.showSaveFilePicker({
        suggestedName,
        types: [{
          description: 'Drakkar Level Files',
          accept: { 'application/octet-stream': ['.000', '.001', '.002', '.003', '.004', '.005', '.006'] },
        }],
      });

      const writable = await handle.createWritable();
      await writable.write(outBuffer);
      await writable.close();
    } catch {
      // User cancelled
    }
  }

  _buildOutputBuffer() {
    // Start from the raw buffer (preserves all unedited bytes)
    const outBuffer = new ArrayBuffer(LEVEL_BYTES);
    const out = new Uint8Array(outBuffer);

    if (this._rawBuffer) {
      out.set(new Uint8Array(this._rawBuffer));
    }

    const state = this.pluginAPI.getState();
    const layers = state.get('layers');
    const dv = new DataView(outBuffer);

    // Read back tile GIDs from the ter/obj layers
    const terData = layers.find(l => l.name === 'Ground')?.data;
    const objData = layers.find(l => l.name === 'Objects')?.data;
    const ter2Data = layers.find(l => l.name === 'Ter2')?.data;
    const obj2Data = layers.find(l => l.name === 'Obj2')?.data;

    // Read back properties from the property layer
    const propsLayer = layers.find(l => l.name === 'Drakkar Properties');
    const propsMap = new Map();
    if (propsLayer?.objects) {
      for (const obj of propsLayer.objects) {
        const match = obj.name?.match(/^cell_(\d+)_(\d+)$/);
        if (match) {
          const px = parseInt(match[1], 10);
          const py = parseInt(match[2], 10);
          propsMap.set(py * GRID_W + px, obj.properties);
        }
      }
    }

    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        const idx = y * GRID_W + x;
        const off = idx * CELL_BYTES;

        // Write ter (page * 256 + tile encoding)
        if (terData) {
          const gid = terData[idx];
          const ter = gid > 0 ? gid - 1 : 0;
          dv.setUint8(off + 17, (ter >> 8) & 0xFF);  // page
          dv.setUint8(off + 18, ter & 0xFF);          // tile
        }

        // Write obj
        if (objData) {
          const gid = objData[idx];
          const obj = gid > 0 ? gid - 1 : 0;
          dv.setUint8(off + 19, (obj >> 8) & 0xFF);
          dv.setUint8(off + 20, obj & 0xFF);
        }

        // Write ter2 and obj2
        if (ter2Data) {
          const gid = ter2Data[idx];
          dv.setUint8(off + 21, gid > 0 ? gid - 1 : 0);
        }
        if (obj2Data) {
          const gid = obj2Data[idx];
          dv.setUint8(off + 22, gid > 0 ? gid - 1 : 0);
        }

        // Write properties if they were edited
        const props = propsMap.get(idx);
        if (props) {
          if (props.terrain_type !== undefined) setU16(dv, off + 0, props.terrain_type);
          if (props.description !== undefined) setU16(dv, off + 4, props.description);
          if (props.scenario !== undefined) setU16(dv, off + 6, props.scenario);
          if (props.visibility !== undefined) dv.setUint8(off + 8, props.visibility);
          if (props.lighting !== undefined) setU16(dv, off + 10, props.lighting);
          if (props.flags !== undefined) dv.setUint32(off + 12, props.flags, true);
          if (props.hex_size !== undefined) dv.setUint8(off + 16, props.hex_size);
        }
      }
    }

    return outBuffer;
  }

  // ---------- Generator/Trap Import ----------

  async _importGeneratorsTraps() {
    const handles = await window.showOpenFilePicker({
      multiple: true,
      types: [{
        description: 'Drakkar Data Lists',
        accept: { 'text/plain': ['.list'] },
      }],
    }).catch(() => []);
    if (!handles || handles.length === 0) return;

    for (const handle of handles) {
      const file = await handle.getFile();
      const text = await file.text();
      const name = file.name.toLowerCase();

      if (name.includes('generator_data')) {
        this._parseGenerators(text);
      } else if (name.includes('trap_data')) {
        this._parseTraps(text);
      }
    }

    this._addGeneratorTrapLayers();
  }

  _parseGenerators(csv) {
    this._generators = [];
    const lines = csv.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#'));

    for (const line of lines) {
      const parts = line.split(',');
      if (parts.length < 7) continue;
      this._generators.push({
        name: parts[0].trim(),
        x: parseInt(parts[1], 10),
        y: parseInt(parts[2], 10),
        level: parseInt(parts[3], 10),
        bias: parseInt(parts[4], 10),
        rounds: parseInt(parts[5], 10),
        max_count: parseInt(parts[6], 10),
      });
    }
  }

  _parseTraps(csv) {
    this._traps = [];
    const lines = csv.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#'));

    for (const line of lines) {
      const parts = line.split(',');
      if (parts.length < 4) continue;
      this._traps.push({
        name: parts[0].trim(),
        x: parseInt(parts[1], 10),
        y: parseInt(parts[2], 10),
        level: parseInt(parts[3], 10),
      });
    }
  }

  _addGeneratorTrapLayers() {
    const state = this.pluginAPI.getState();
    const layers = [...state.get('layers')];
    const tileW = state.get('tileWidth') || TILE_PIXEL_SIZE;
    const tileH = state.get('tileHeight') || TILE_PIXEL_SIZE;

    // Filter to current level if known
    const levelFilter = this._currentLevel >= 0
      ? (entry) => entry.level === this._currentLevel
      : () => true;

    // Generator layer
    const genObjects = this._generators.filter(levelFilter).map((g, i) => ({
      id: 100000 + i,
      name: g.name,
      type: 'drakkar_generator',
      x: g.x * tileW,
      y: g.y * tileH,
      width: tileW,
      height: tileH,
      properties: {
        generatorName: g.name,
        bias: g.bias,
        rounds: g.rounds,
        max_count: g.max_count,
        level: g.level,
      },
    }));

    if (genObjects.length > 0) {
      // Remove existing generator layer if present
      const existingIdx = layers.findIndex(l => l.name === 'Generators');
      if (existingIdx >= 0) layers.splice(existingIdx, 1);

      layers.push({
        name: 'Generators',
        type: 'objectgroup',
        visible: true,
        opacity: 1,
        objects: genObjects,
      });
    }

    // Trap layer
    const trapObjects = this._traps.filter(levelFilter).map((t, i) => ({
      id: 200000 + i,
      name: t.name,
      type: 'drakkar_trap',
      x: t.x * tileW,
      y: t.y * tileH,
      width: tileW,
      height: tileH,
      properties: {
        trapName: t.name,
        level: t.level,
      },
    }));

    if (trapObjects.length > 0) {
      const existingIdx = layers.findIndex(l => l.name === 'Traps');
      if (existingIdx >= 0) layers.splice(existingIdx, 1);

      layers.push({
        name: 'Traps',
        type: 'objectgroup',
        visible: true,
        opacity: 1,
        objects: trapObjects,
      });
    }

    state.set('layers', layers);
    this.pluginAPI.requestRender();
  }

  // ---------- DAT/NDX Tile Loading ----------

  /** Load NDX file (call from external code or a future menu item). */
  async loadNDX(buffer) {
    this._tileIndex.parse(buffer);
    this._tileLoader.setIndex(this._tileIndex);
  }

  /** Load DAT file buffer. */
  async loadDAT(buffer) {
    this._tileLoader.setDATBuffer(buffer);
  }

  /** Build tileset images from loaded tiles and add to state. */
  _buildTilesetFromLoadedTiles() {
    if (!this._tileLoader.isReady || !this._cells) return;

    // Collect unique ter/obj values from the map
    const terSet = new Set();
    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        const cell = this._cells[y][x];
        const ter = terValue(cell.ter_page, cell.ter_tile);
        const obj = objValue(cell.obj_page, cell.obj_tile);
        if (ter > 0) terSet.add(ter);
        if (obj > 0) terSet.add(obj);
      }
    }

    const terValues = [...terSet].sort((a, b) => a - b);
    if (terValues.length === 0) return;

    const canvas = this._tileLoader.createTilesetImage(terValues);
    const img = new Image();
    img.src = canvas.toDataURL();
    img.onload = () => {
      const state = this.pluginAPI.getState();
      const tilesetImages = state.get('tilesetImages') || [];
      tilesetImages.push({
        name: 'Drakkar Tiles',
        image: img,
        tileWidth: TILE_PIXEL_SIZE,
        tileHeight: TILE_PIXEL_SIZE,
        firstGid: 1,
      });
      state.set('tilesetImages', tilesetImages);
    };
  }

  // ---------- Overlays ----------

  _toggleVisOverlay() {
    this._showVisOverlay = !this._showVisOverlay;
    this.pluginAPI.requestRender();
  }

  _toggleFlagsOverlay() {
    this._showFlagsOverlay = !this._showFlagsOverlay;
    this.pluginAPI.requestRender();
  }

  _toggleWallMerge() {
    this._showWallMerge = !this._showWallMerge;
    this.pluginAPI.requestRender();
  }

  _renderOverlays(ctx, state) {
    if (!this._cells) return;

    const tileW = state.get('tileWidth') || TILE_PIXEL_SIZE;
    const tileH = state.get('tileHeight') || TILE_PIXEL_SIZE;

    if (this._showVisOverlay) {
      this._renderVisibilityOverlay(ctx, tileW, tileH);
    }

    if (this._showFlagsOverlay) {
      this._renderFlagsOverlay(ctx, tileW, tileH);
    }

    if (this._showWallMerge) {
      this._renderWallMergeOverlay(ctx, tileW, tileH);
    }
  }

  _renderVisibilityOverlay(ctx, tileW, tileH) {
    const colors = {
      1: 'rgba(255, 0, 0, 0.3)',    // Blocked - red
      2: 'rgba(255, 255, 0, 0.3)',   // Obstructed - yellow
      3: 'rgba(0, 255, 0, 0.15)',    // Normal - green
    };

    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        const cell = this._cells[y][x];
        const vis = effectiveVisibility(cell.visibility, cell.terrain_type);
        const color = colors[vis];
        if (color) {
          ctx.fillStyle = color;
          ctx.fillRect(x * tileW, y * tileH, tileW, tileH);
        }
      }
    }
  }

  _renderFlagsOverlay(ctx, tileW, tileH) {
    ctx.font = `${Math.max(8, tileH * 0.15)}px monospace`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        const cell = this._cells[y][x];
        if (cell.flags === 0) continue;

        ctx.fillStyle = 'rgba(200, 100, 255, 0.3)';
        ctx.fillRect(x * tileW, y * tileH, tileW, tileH);

        const active = formatFlags(cell.flags);
        if (active.length > 0) {
          ctx.fillStyle = '#fff';
          // Show abbreviated flag names
          const abbrev = active.map(f => f.substring(0, 3)).join(' ');
          ctx.fillText(abbrev, x * tileW + 2, y * tileH + 2);
        }
      }
    }
  }

  // ---------- Wall Merge ----------

  _renderWallMergeOverlay(ctx, tileW, tileH) {
    if (!this._tileLoader.isReady) {
      // Even without tiles, show merge bitmap as debug text
      this._renderWallMergeDebug(ctx, tileW, tileH);
      return;
    }

    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        const merged = this._getMergedObj(x, y);
        if (merged === null) continue;

        const imageData = this._tileLoader.loadTile(merged);
        if (imageData) {
          // Create temp canvas for the imageData and draw it
          const tmpCanvas = document.createElement('canvas');
          tmpCanvas.width = TILE_PIXEL_SIZE;
          tmpCanvas.height = TILE_PIXEL_SIZE;
          tmpCanvas.getContext('2d').putImageData(imageData, 0, 0);
          ctx.drawImage(tmpCanvas, x * tileW, y * tileH, tileW, tileH);
        }
      }
    }
  }

  _renderWallMergeDebug(ctx, tileW, tileH) {
    ctx.font = `${Math.max(8, tileH * 0.2)}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        const merged = this._getMergedObj(x, y);
        if (merged === null) continue;

        const offset = merged & 0xFF;
        ctx.fillStyle = 'rgba(100, 100, 255, 0.3)';
        ctx.fillRect(x * tileW, y * tileH, tileW, tileH);
        ctx.fillStyle = '#fff';
        ctx.fillText(`W${offset}`, x * tileW + tileW / 2, y * tileH + tileH / 2);
      }
    }
  }

  /**
   * Get the merged obj value for a cell, or null if it doesn't participate in merge.
   * Implements the wall auto-merge algorithm from DEVELOPER_NOTES.md.
   */
  _getMergedObj(x, y) {
    const cell = this._cells[y][x];
    const objVal = objValue(cell.obj_page, cell.obj_tile);
    const baseTile = objVal & 0xFF;

    if (!WALL_MERGE_BASE_TILES.has(baseTile)) return null;

    const page = cell.obj_page;

    // Check 4 cardinal neighbors for wall-compatible terrain types
    let cardinal = 0;
    if (this._isWallCompatible(x, y - 1)) cardinal |= 1; // N = bit 0
    if (this._isWallCompatible(x + 1, y)) cardinal |= 2; // E = bit 1
    if (this._isWallCompatible(x, y + 1)) cardinal |= 4; // S = bit 2
    if (this._isWallCompatible(x - 1, y)) cardinal |= 8; // W = bit 3

    // Check diagonals only when 2+ cardinals present
    let diagBits = 0;
    const cardinalCount = (cardinal & 1) + ((cardinal >> 1) & 1) + ((cardinal >> 2) & 1) + ((cardinal >> 3) & 1);
    if (cardinalCount >= 2) {
      // NE diagonal (when N and E are both present)
      if ((cardinal & 0x03) === 0x03 && this._isWallCompatible(x + 1, y - 1)) diagBits |= 1;
      // SE diagonal (when E and S are both present)
      if ((cardinal & 0x06) === 0x06 && this._isWallCompatible(x + 1, y + 1)) diagBits |= 2;
      // SW diagonal (when S and W are both present)
      if ((cardinal & 0x0C) === 0x0C && this._isWallCompatible(x - 1, y + 1)) diagBits |= 4;
      // NW diagonal (when N and W are both present)
      if ((cardinal & 0x09) === 0x09 && this._isWallCompatible(x - 1, y - 1)) diagBits |= 8;
    }

    // Use cardinal-only lookup (diagonal lookup is sparse, fall back to cardinal)
    const offset = WALL_MERGE_OFFSETS_CARDINAL[cardinal];
    return (page << 8) | (baseTile + offset);
  }

  _isWallCompatible(x, y) {
    // Map boundaries count as walls
    if (x < 0 || x >= GRID_W || y < 0 || y >= GRID_H) return true;
    return WALL_MERGE_TER_TYPES.has(this._cells[y][x].terrain_type);
  }
}
